import { Directive, inject, Input, OnInit, signal } from '@angular/core';
import { FormGroup, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import {
  CreateDriverBlendedPatientDistributionInput,
  createDriverBlendedPatientDistributionMutation,
  CreateDriverBlendedSiteDistributionInput,
  createDriverBlendedSiteDistributionMutation,
  UpdateDriverBlendedPatientDistributionInput,
  updateDriverBlendedPatientDistributionMutation,
  UpdateDriverBlendedSiteDistributionInput,
  updateDriverBlendedSiteDistributionMutation,
} from '@shared/services/gql.service';

import { BlendedCurveModalDataModel } from './blended-curve-modal-data.model';

@Directive()
export abstract class BlendedCurveModalDirective<
  T extends { name: string },
  Z extends { id: string },
> implements OnInit
{
  loading = signal(false);

  @Input() availableGroups: (T & {
    check: boolean;
  })[] = [];
  blendedCurveForm!: FormGroup;
  abstract constituentGroups: Z[];
  formBuilder!: UntypedFormBuilder;
  labelButton = 'Create';
  labelTopic = 'Create';
  ref!: CustomOverlayRef<unknown, BlendedCurveModalDataModel<T>>;
  selectedCurveIds: string[] = [];

  ngOnInit(): void {
    this.loading.set(true);

    if (this.ref.data) {
      this.init();
    }

    this.loading.set(false);
  }

  constructor() {
    this.formBuilder = inject(UntypedFormBuilder);
    this.blendedCurveForm = this.formBuilder.group({
      blendedCurveName: '',
    });
  }

  abstract createData():
    | CreateDriverBlendedPatientDistributionInput
    | CreateDriverBlendedSiteDistributionInput;

  abstract createDriverBlendedDistribution(): Promise<
    GraphqlResponse<
      createDriverBlendedPatientDistributionMutation | createDriverBlendedSiteDistributionMutation
    >
  >;

  abstract getGroupId(item: T): string;

  private init(): void {
    if (this.constituentGroups.length) {
      this.constituentGroups.forEach((x) => {
        this.selectedCurveIds.push(x.id);
      });
    }

    if (this.ref.data?.blendedCurve) {
      this.labelTopic = 'Edit';
      this.labelButton = 'Update';

      const blendedCurveName = this.ref.data.blendedCurve.name;

      this.blendedCurveForm.setValue({ blendedCurveName });

      this.ref.data.availableGroups.forEach((availableGroup) => {
        const curve = this.constituentGroups.find((constituentGroup) => {
          return constituentGroup.id === this.getGroupId(availableGroup);
        });

        this.availableGroups.push({ ...availableGroup, check: !!curve });
      });
    } else {
      this.ref.data?.availableGroups.forEach((availableGroup) => {
        this.availableGroups.push({ ...availableGroup, check: false });
      });
    }
  }

  async saveBlendedCurve(): Promise<void> {
    if (this.blendedCurveForm.invalid) {
      return;
    }

    this.loading.set(true);
    let response;

    if (this.ref.data?.blendedCurve) {
      response = await this.updateDriverBlendedDistribution();
    } else {
      response = await this.createDriverBlendedDistribution();
    }

    this.loading.set(false);

    if (response.success) {
      this.ref.close({ data: response?.data });
    }
  }

  setValue(item: T, checked = false): void {
    const index = this.selectedCurveIds.findIndex((x: string) => {
      return x === this.getGroupId(item);
    });

    if (checked) {
      if (index === -1) {
        this.selectedCurveIds.push(this.getGroupId(item));
      }
    } else if (index !== -1) {
      this.selectedCurveIds.splice(index, 1);
    }
  }

  get subTitle(): string {
    return this.ref.data?.text.subTitle || '';
  }

  get title(): string {
    return this.ref.data?.text.title || '';
  }

  abstract updateData():
    | UpdateDriverBlendedPatientDistributionInput
    | UpdateDriverBlendedSiteDistributionInput;

  abstract updateDriverBlendedDistribution(): Promise<
    GraphqlResponse<
      updateDriverBlendedPatientDistributionMutation | updateDriverBlendedSiteDistributionMutation
    >
  >;

  get validators(): ValidatorFn[] {
    return [
      Validators.required,
      CustomValidators.valueNotInList(
        this.ref.data?.existingCurvesNames || [],
        'duplicatedName',
        true
      ),
    ];
  }
}
